// Seamless marquee custom element.
// A CSS keyframe drives the scroll; hover and keyboard focus slow/stop the marquee
// by ramping the animation playbackRate to 0 and back to 1 via requestAnimationFrame.
//
// Expected markup (see sections/ticker.liquid, announcement-bar.liquid, logo-list.liquid):
//   <marquee-ticker data-speed data-stop-on-hover [data-pause-target] style="--marquee-direction:…">
//     <div class="marquee__wrapper">
//       <div class="marquee__content">
//         <div class="marquee__items">…content rendered once…</div>
//       </div>
//     </div>
//   </marquee-ticker>

(() => {
  const RAMP_DURATION = 500;
  // Breathing room around a focused item so its focus ring is never edge-flush.
  const REVEAL_MARGIN = 16;
  const LOOP_EDGE = 0.0001;
  const FOCUSABLE =
    "a[href], button, input, select, textarea, iframe, [tabindex]:not([tabindex='-1'])";
  // smoothstep — ease-in-out so the speed change has no hard edges
  const easing = (t) => t * t * (3 - 2 * t);

  // Animate a value from -> to over duration and expose the live value + a cancel handle.
  function animateValue({ from, to, duration, onUpdate, onComplete }) {
    const start = performance.now();
    let current = from;
    let cancelled = false;

    const frame = (now) => {
      if (cancelled) return;
      const progress = Math.min((now - start) / duration, 1);
      current = from + (to - from) * easing(progress);
      onUpdate(current);
      if (progress < 1) requestAnimationFrame(frame);
      else if (onComplete) onComplete();
    };
    requestAnimationFrame(frame);

    return {
      get current() {
        return current;
      },
      cancel() {
        cancelled = true;
      },
    };
  }

  function debounce(fn, wait) {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  }

  class MarqueeTicker extends HTMLElement {
    #wrapper = null;
    #content = null;
    #items = null;
    #template = null; // pristine single item, captured once, survives re-connects
    #generation = 0; // invalidates in-flight async builds when the element re-connects
    #hoverTarget = null;
    #ramp = null; // playbackRate ramp handle
    #containerWidth = null;
    #hovered = false;
    #focused = false;
    #visibility = null; // IntersectionObserver gate
    #visible = true;

    async connectedCallback() {
      this.#wrapper = this.querySelector(".marquee__wrapper");
      this.#content = this.querySelector(".marquee__content");
      if (!this.#wrapper || !this.#content) return;

      if (!this.#template) {
        const firstItem = this.#content.querySelector(".marquee__items");
        if (!firstItem) return;
        this.#template = firstItem.cloneNode(true);
      }

      // --marquee-direction keeps the section setting's own value and the mirror
      // lands in --marquee-run-direction, so the flip is idempotent: re-connecting
      // this element, or cloning its markup the way the theme editor does, cannot
      // read a mirrored value back as the base and flip it a second time.
      const baseDirection =
        this.style.getPropertyValue("--marquee-direction").trim() || "normal";
      const mirrored = baseDirection === "reverse" ? "normal" : "reverse";
      this.style.setProperty(
        "--marquee-run-direction",
        document.documentElement.dir === "rtl" ? mirrored : baseDirection
      );

      const generation = ++this.#generation;
      this.#reset();

      // Fonts change item widths, which changes how many copies fill the row —
      // measure only once they are ready to avoid a re-fill/jump on swap.
      if (document.fonts && document.fonts.status !== "loaded") {
        try {
          await document.fonts.ready;
        } catch (e) {
          // ignore
        }
      }
      if (!this.isConnected || generation !== this.#generation) return;

      const { copies } = this.#queryCopies();

      this.#addCopies(copies - 1);
      this.#duplicateContent();
      this.#setSpeed(copies);

      window.addEventListener("resize", this.#onResize);
      this.addEventListener("scroll", this.#onScroll, { passive: true });
      this.addEventListener("focusin", this.#onFocusIn);
      this.addEventListener("focusout", this.#onFocusOut);
      this.#observeVisibility();

      if (
        this.dataset.stopOnHover === "true" &&
        window.matchMedia("(hover: hover)").matches
      ) {
        this.#hoverTarget =
          (this.dataset.pauseTarget &&
            this.closest(this.dataset.pauseTarget)) ||
          this;
        this.#hoverTarget.addEventListener(
          "pointerenter",
          this.#onPointerEnter
        );
        this.#hoverTarget.addEventListener(
          "pointerleave",
          this.#onPointerLeave
        );
      }
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.#onResize);
      this.#onResize.cancel();
      this.removeEventListener("scroll", this.#onScroll);
      this.removeEventListener("focusin", this.#onFocusIn);
      this.removeEventListener("focusout", this.#onFocusOut);
      this.#hovered = false;
      this.#focused = false;
      if (this.#hoverTarget) {
        this.#hoverTarget.removeEventListener(
          "pointerenter",
          this.#onPointerEnter
        );
        this.#hoverTarget.removeEventListener(
          "pointerleave",
          this.#onPointerLeave
        );
        this.#hoverTarget = null;
      }
      this.#ramp?.cancel();
      this.#ramp = null;
      this.#visibility?.disconnect();
      this.#visibility = null;
      this.#visible = true;
    }

    #reset() {
      this.#ramp?.cancel();
      this.#ramp = null;
      this.#contentClone?.remove();
      this.#content.replaceChildren(this.#template.cloneNode(true));
      this.#items = this.#content.firstElementChild;
      this.style.removeProperty("--marquee-speed");
    }

    get #animation() {
      return this.#wrapper?.getAnimations()[0] ?? null;
    }

    // A running CSS animation costs the document a style recalculation every frame
    // for as long as it runs, so a marquee scrolled out of view would keep the whole
    // page recalculating at 60fps forever. The margin resumes it just before it
    // arrives, so it is never seen standing still.
    #observeVisibility() {
      this.#visibility?.disconnect();
      if (typeof IntersectionObserver !== "function") return;
      this.#visibility = new IntersectionObserver(
        (entries) => {
          this.#visible = entries[entries.length - 1].isIntersecting;
          this.#syncVisibility();
        },
        { rootMargin: "200px 0px" }
      );
      this.#visibility.observe(this);
    }

    // Paused, not rate-zeroed: an animation held at playbackRate 0 still counts as
    // running and keeps ticking the frame loop.
    #syncVisibility() {
      const animation = this.#animation;
      if (!animation) return; // reduced-motion: no animation to control
      if (this.#visible) {
        if (animation.playState === "paused") animation.play();
      } else if (animation.playState === "running") {
        animation.pause();
      }
    }

    #onPointerEnter = () => {
      this.#hovered = true;
      this.#syncRate();
    };

    #onPointerLeave = () => {
      this.#hovered = false;
      this.#syncRate();
    };

    #onFocusIn = (event) => {
      this.#focused = true;
      // Stop dead instead of ramping: the row must hold still around the item
      // #reveal parks, and gliding on for half a second reads as a missed target.
      this.#ramp?.cancel();
      this.#ramp = null;
      const animation = this.#animation;
      if (animation) animation.playbackRate = 0;

      this.scrollLeft = 0;
      this.#reveal(event.target);
    };

    #onFocusOut = (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.#focused = false;
      this.#syncRate();
    };

    #syncRate() {
      this.#rampTo(this.#hovered || this.#focused ? 0 : 1);
    }

    get #direction() {
      return this.style.getPropertyValue("--marquee-run-direction").trim();
    }

    // Focusing a link makes the browser scroll this overflow:hidden box to show it,
    // which slides the row out from under the loop and tears a gap open at the edge.
    // Keep the box pinned — #reveal does the reveal. Reduced motion has no loop to
    // drive, so there the native scroll is the only way through and is left alone.
    #onScroll = () => {
      if (this.scrollTop !== 0) this.scrollTop = 0;
      if (this.scrollLeft !== 0 && this.#animation) this.scrollLeft = 0;
    };

    // Drive the loop itself to the focused item — a native scroll cannot reach it
    // anyway once the row has travelled past the left edge (scrollLeft stops at 0).
    #reveal(element) {
      if (!this.#animation || !this.contains(element)) return;

      const host = this.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      if (!rect.width) return;

      const margin = Math.min(
        REVEAL_MARGIN,
        Math.max(0, (host.width - rect.width) / 2)
      );

      let shift = 0;
      if (rect.left < host.left + margin) shift = rect.left - host.left - margin;
      else if (rect.right > host.right - margin)
        shift = rect.right - host.right + margin;
      if (!shift) return;

      this.#setOffset(this.#currentOffset() + shift);
    }

    #setOffset(offset) {
      const animation = this.#animation;
      const period = this.#loopDistance();
      const duration = animation?.effect?.getTiming?.().duration;
      if (!period || typeof duration !== "number" || !duration) return;

      // Clamp, never wrap: an offset one loop away looks identical but parks the
      // focused item off-screen with only its copy standing in the visible spot.
      const progress = Math.min(Math.max(offset, 0), period) / period;
      const directed = this.#direction === "reverse" ? 1 - progress : progress;
      // A full duration is the next iteration's start, i.e. a whole loop away.
      animation.currentTime = Math.min(directed, 1 - LOOP_EDGE) * duration;
    }

    // How far the row has already travelled left, in px.
    #currentOffset() {
      const transform = getComputedStyle(this.#wrapper).transform;
      if (!transform || transform === "none") return 0;
      try {
        return -new DOMMatrixReadOnly(transform).m41;
      } catch (e) {
        return 0;
      }
    }

    // One keyframe travels a single content width + gap; the wrapper holds two copies.
    #loopDistance() {
      const width = this.#wrapper.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.#wrapper).columnGap) || 0;
      return (width + gap) / 2;
    }

    #rampTo(target) {
      const animation = this.#animation;
      if (!animation) return; // reduced-motion: no animation to control

      const from = this.#ramp?.current ?? animation.playbackRate;
      this.#ramp?.cancel();
      this.#ramp = animateValue({
        from,
        to: target,
        duration: RAMP_DURATION,
        // Assign playbackRate directly, not updatePlaybackRate: Safari's async
        // rate update desyncs currentTime on repeated calls and visually
        // scrolls the marquee backwards while ramping to a pause.
        onUpdate: (value) => {
          animation.playbackRate = value;
        },
        onComplete: () => {
          this.#ramp = null;
        },
      });
    }

    #setSpeed(copies) {
      // ticker_speed 1..30 (30 = fastest). Invert to a factor; more copies =>
      // longer loop, so sqrt keeps the perceived speed close across widths.
      const speed = Number(this.dataset.speed) || 15;
      const speedFactor = (31 - speed) * 1.5;
      const duration = Math.sqrt(copies) * speedFactor;
      this.style.setProperty("--marquee-speed", `${duration}s`);
    }

    #queryCopies() {
      const rootWidth = this.getBoundingClientRect().width;
      const itemWidth = this.#items
        ? this.#items.getBoundingClientRect().width
        : 0;
      const widthChanged = this.#containerWidth !== rootWidth;
      this.#containerWidth = rootWidth;

      return {
        copies: itemWidth === 0 ? 1 : Math.ceil(rootWidth / itemWidth),
        widthChanged,
      };
    }

    #addCopies(count) {
      if (!this.#items || count < 1) return;
      for (let i = 0; i < count; i++) {
        this.#content.appendChild(this.#hideCopy(this.#items.cloneNode(true)));
      }
    }

    // A copy is decoration: out of the a11y tree and out of the tab order, so Tab
    // visits every item once instead of cycling the same links per copy. Clicks
    // still work, since the copies are what is on screen most of the time.
    #hideCopy(clone) {
      clone.setAttribute("aria-hidden", "true");
      for (const element of clone.querySelectorAll(FOCUSABLE)) {
        element.setAttribute("tabindex", "-1");
      }
      return clone;
    }

    #removeCopies(count) {
      // Always keep at least one item block in the content.
      const removable = Math.min(count, this.#content.children.length - 1);
      for (let i = 0; i < removable; i++) {
        this.#content.lastElementChild?.remove();
      }
    }

    get #contentClone() {
      const last = this.#wrapper.lastElementChild;
      return this.#content !== last ? last : null;
    }

    // The seamless half: a full copy of the content trailing the original.
    #duplicateContent() {
      this.#contentClone?.remove();
      this.#wrapper.appendChild(this.#hideCopy(this.#content.cloneNode(true)));
    }

    #onResize = debounce(() => {
      if (!this.isConnected || !this.#items) return;

      const { copies, widthChanged } = this.#queryCopies();
      if (!widthChanged) return;

      const current = this.#content.children.length;
      if (copies > current) this.#addCopies(copies - current);
      else if (copies < current) this.#removeCopies(current - copies);

      this.#duplicateContent();
      this.#setSpeed(copies);
      this.#restartAnimation();
    }, 250);

    #restartAnimation() {
      this.#ramp?.cancel();
      this.#ramp = null;
      const animations = this.#wrapper.getAnimations();
      requestAnimationFrame(() => {
        for (const animation of animations) {
          animation.currentTime = 0;
          animation.updatePlaybackRate(1);
        }
      });
    }
  }

  if (!customElements.get("marquee-ticker")) {
    customElements.define("marquee-ticker", MarqueeTicker);
  }
})();
